export const exitReadiness = {
  slug: "exit-readiness",
  n: "00",
  title: "Founder's Exit Readiness",
  tagline: "Prepare the business — and the founder — for a clean, well-priced exit.",
  deck:
    "A pre-exit diagnostic followed by an embedded workstream that installs the operating discipline a sophisticated buyer expects.",
  situation:
    "You are 12–36 months from a possible exit. The business runs because you run it. Forecasts are gut-feel and management depth thins two layers below the founder.",
  response:
    "We diagnose the gap, then embed alongside the leadership team — six to eighteen months, one to three days a week — to close it.",
  image: "/images/service-exit-readiness.jpg",
  alt: "Founder's exit readiness — primary practice",
  tags: ["12–36 month horizon", "Embedded", "Diligence-ready"],
  outcomes: [
    "Reduced founder dependency",
    "Cleaner financials & forecasting",
    "Deeper management bench",
    "Documented operating model",
    "Defensible growth narrative",
    "Smoother diligence, defended valuation",
  ],
  longContent: [
    {
      heading: "Who this is for",
      body:
        "Founders of profitable, owner-led businesses — typically £5m–£100m revenue — twelve to thirty-six months from a potential transaction: strategic sale, PE recap, MBO or succession.",
    },
    {
      heading: "The diagnostic",
      body:
        "Four to eight weeks. A structured read on how the business actually runs versus how a sophisticated buyer will see it. Output: a written diagnostic and a sequenced workplan to close the gaps that matter.",
    },
    {
      heading: "The embedded work",
      body:
        "Six to eighteen months. We install the operating cadence a buyer expects: a real management rhythm, forecasting that holds together under pressure, documented playbooks for what currently sits in the founder's head, and a leadership bench that can run the business without you in the room.",
    },
    {
      heading: "What changes by the end",
      body:
        "You enter diligence with a business that reads cleanly. The valuation gap closes. Earn-outs shrink. Optionality expands.",
    },
    {
      heading: "Commercials",
      body:
        "Diagnostic: fixed fee. Embedded engagement: fixed monthly fee for a fixed cadence, three-month minimum. One client at a time on embedded work.",
    },
  ],
};

export const services = [
  {
    slug: "fractional-leadership",
    n: "01",
    title: "Interim & Fractional Leadership",
    deck:
      "Senior operating leadership embedded in your business — through transformation, between hires, or where the role doesn't justify a permanent appointment.",
    situation:
      "You need an experienced operator running something — without committing to a permanent hire.",
    response:
      "We embed for three to twelve months, typically one to three days a week. Accountable for outcomes, not just advice.",
    image: "/images/service-fractional.jpg",
    alt: "Interim and fractional leadership",
    tags: ["Embedded", "3–12 months", "1–3 days/week"],
    longContent: [
      {
        heading: "Embedded, not advisory",
        body:
          "We work to your operating rhythm — in your standups, your weekly reviews, your skip-levels. Accountable for the outcome, not handing you a deck.",
      },
      {
        heading: "Where this works",
        body:
          "Businesses between £10m and £200m revenue going through a scope expansion, leadership gap, turnaround, or operational redesign.",
      },
      {
        heading: "Commercials",
        body:
          "Fixed monthly fee for a fixed cadence. Three-month minimum, twelve-month maximum.",
      },
    ],
  },
  {
    slug: "diagnostics",
    n: "02",
    title: "Operating Diagnostics & Strategy",
    deck:
      "A structured read on how the business actually runs. Output: a sequenced set of high-leverage moves and the change plan to land them.",
    situation:
      "You sense there's more in the business than current performance shows but can't pinpoint where the friction lives.",
    response:
      "Four to eight weeks. A written diagnostic and a sequenced workplan you can act on.",
    image: "/images/service-diagnostics.jpg",
    alt: "Operating diagnostics and strategy",
    tags: ["Diagnostic", "Roadmap", "Change plan"],
    longContent: [
      {
        heading: "What we cover",
        body:
          "Where work actually happens vs where the org chart says it does. Where decisions are made and where they should be. Where the data is honest, where it is theatre.",
      },
      {
        heading: "What you get",
        body:
          "A 12–20 page written diagnostic. A small set of high-leverage moves, sequenced by dependency and impact. Owners, milestones, leading indicators.",
      },
      {
        heading: "Timeline",
        body:
          "Four to eight weeks. Fixed price agreed up front.",
      },
    ],
  },
  {
    slug: "ai-workflow",
    n: "03",
    title: "AI & Workflow Automation",
    deck:
      "Redesign the workflows where AI compresses work that previously needed headcount — then upskill the people who remain into higher-leverage roles.",
    situation:
      "AI is reshaping your industry. Your team uses it ad hoc, but your operating model hasn't been redesigned around it.",
    response:
      "Identify the two or three workflows where leverage is real, redesign end-to-end with AI in the loop, pilot, measure, redesign again.",
    image: "/images/service-ai.jpg",
    alt: "AI and workflow automation",
    tags: ["Redesign", "Automation", "Upskill"],
    longContent: [
      {
        heading: "Why most AI rollouts underperform",
        body:
          "Because they are tooling projects, not operating-model projects. Individual productivity rises 10–20%; the structure of work does not change.",
      },
      {
        heading: "How we approach it",
        body:
          "Pick two or three high-volume, structured workflows. Redesign end-to-end with AI in the loop. Pilot, measure honestly, redesign again. Move operators whose roles compressed into higher-leverage work.",
      },
      {
        heading: "What we won't do",
        body:
          "Sell you a chatbot. Pitch a model. The bottleneck is workflow design and change management, not tooling.",
      },
    ],
  },
];

export const insights = [
  {
    slug: "ai-is-a-workflow-problem",
    title: "AI is a workflow problem, not a tooling problem",
    dek:
      "The teams getting real leverage from AI redesigned the work — not just the tools.",
    date: "Dec 2025",
    readTime: "4 min read",
    body: [
      "Every operating leader has the same story: their team has adopted AI, individual productivity is up somewhere between 10 and 25 percent, and almost no one can point to where it has changed the shape of the business.",
      "Productivity gains at the level of the individual do not compound at the level of the system unless someone redesigns the system. A CS rep handling 12% more tickets per day inside an unchanged queue structure gives you a slightly faster version of the same operating model — not a redesigned one.",
      "The work to capture real AI leverage is not installing better tools. It is identifying which workflows can be compressed, which can be eliminated, and which need to be reinvented with AI in the loop from the start. That work sits in the awkward gap between the COO's remit and the CTO's remit — which is one reason it does not get done.",
      "If you want to know whether your business is capturing real AI leverage, do not look at the adoption rate. Look at headcount ratios in the workflows AI has touched. Look at cycle times. Look at cost-per-unit on the work that used to be most expensive.",
    ],
  },
  {
    slug: "the-myth-of-the-full-time-hire",
    title: "The myth of the full-time hire",
    dek:
      "Most operating gaps are not full-time gaps. The wrong hire often costs more than it saves.",
    date: "Nov 2025",
    readTime: "3 min read",
    body: [
      "There is a default reflex in mid-market businesses: when a function strains, hire a senior leader to run it. Sometimes that is right. Often it is not.",
      "The questions worth asking first: Is this a permanent gap or a transitional one? Is the work you would hand a full-time leader actually full-time work — or is 60% of it sequencing a transformation that ends in 12 months? Will the seniority you need to fix the problem be the seniority you need to run it once fixed?",
      "When the answer to the second or third is no, a fractional engagement tends to land the change faster, at lower total cost, and without the political damage of hiring someone senior into a role you then have to shrink. The right hire at the wrong time is one of the most expensive operating mistakes you can make.",
    ],
  },
  {
    slug: "ramp-time-as-leading-indicator",
    title: "Ramp time as a leading indicator",
    dek:
      "If ramp time hasn't improved in three years, it's the clearest signal something is structurally off.",
    date: "Oct 2025",
    readTime: "5 min read",
    body: [
      "Ask any operating leader how long it takes a new hire in their highest-volume role to reach full productivity. Most have a rough answer. Almost none can tell you whether that number has improved or worsened over three years.",
      "Ramp time is the closest thing operations has to a single composite health metric. It captures training pipeline quality, process clarity, tooling legibility, SOP maturity, and the leverage your most experienced operators have over the system. When ramp times stagnate, every one of those is leaking — slowly, invisibly, without showing up on a dashboard.",
      "The most under-priced operational investment most businesses can make is shortening ramp time by 20 percent in their highest-volume role. If you hire 200 people a year and each ramps in 90 days instead of 120, you have bought 6,000 productive operator-days without spending a penny on headcount. That is a structural margin shift.",
    ],
  },
  {
    slug: "embedded-vs-advising",
    title: "What 'embedded' actually means",
    dek:
      "The difference between a consultant in the room and a consultant on the call is accountability — not presence.",
    date: "Sep 2025",
    readTime: "3 min read",
    body: [
      "'Embedded' has become consulting marketing language. The actual definition matters because the difference between embedded work and advisory work is the difference between change landing and change not landing.",
      "Embedded means in the same meetings the operators are in — not the executive readouts. It means writing the Friday note. It means being on the WhatsApp thread when something breaks on a Sunday, not waiting for a calendar slot on Wednesday. Name on the outcome, not on the deck.",
      "Most operational change fails for the same reason: someone wrote the plan, someone else was supposed to land it, and plan and landing drifted apart. Embedded work collapses both roles into one accountability.",
    ],
  },
];
